import {
    DAuditInfo,
    IAuditStamps,
    RhFormWrapper,
    RhTabsWrapper,
    RHTextField,
} from '../../../../../dum';
import { OrderProductionTypeInput } from './inputs/order-production-type/order-production-type-input';
import { BranchInput } from './inputs/branch/branch-input';
import { EmployeeInactiveInput } from './inputs/is-inactive/is-inactive-input';
import React, { useMemo } from 'react';
import {
    ActivityEntityName,
    useUpsertEmployeeDialogMutation,
} from '../../../../../../services';
import { useHistorialTab } from '../../../../historial';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import { EmployeeIsLeaderInput } from './inputs/is-leader/is-leader-input';
import { FIELD_LABELS, shortLabel } from '../../../../../../helpers';

export interface IEmployee {
    id?: number | null;
    first_name: string;
    last_name: string;
    order_production_type_id?: number | null;
    // true = dado de baja (replaces the old employee_status dropdown).
    is_inactive: boolean;
    branch_id?: number | null;
    is_leader: number | boolean;
    // Base weekly pay + the schedule it assumes (48 / 52.5). Payroll period
    // creation snapshots these onto each entry.
    base_salary: number;
    hours_should_work: number;
}

interface IEmployeeForm {
    // Audit stamps ride alongside the form values — display only, never
    // submitted, and deliberately kept out of IEmployee (see IAuditStamps).
    employee?: (IEmployee & IAuditStamps) | null;
    onSuccess: () => void;
    disabled?: boolean;
}

export const IS_LEADER: keyof IEmployee = 'is_leader';
export const IS_INACTIVE: keyof IEmployee = 'is_inactive';

export function EmployeeForm({
    employee,
    onSuccess,
    disabled = false,
}: IEmployeeForm) {
    const [mutation, { loading }] = useUpsertEmployeeDialogMutation();

    const historialTab = useHistorialTab({
        entityName: ActivityEntityName.Employee,
        entityId: employee?.id,
        unsavedMessage:
            'El historial aparece una vez que el empleado ha sido guardado.',
        emptyMessage: 'No hay actividades registradas para este empleado.',
    });

    const initialValues: IEmployee = useMemo<IEmployee>(() => {
        return {
            id: employee?.id || undefined,
            first_name: employee?.first_name || '',
            last_name: employee?.last_name || '',
            order_production_type_id:
                employee?.order_production_type_id || null,
            is_inactive: employee?.is_inactive || false,
            branch_id: employee?.branch_id || null,
            is_leader: employee?.is_leader === 1 || false,
            base_salary: employee?.base_salary ?? 0,
            hours_should_work: employee?.hours_should_work ?? 0,
        };
    }, [employee]);

    const validationSchema: SchemaOf<IEmployee> = yup.object({
        id: yup.number().notRequired().nullable(),
        first_name: yup.string().required(`'Nombre' es obligatorio`),
        last_name: yup.string().required(`'Apellidos' es obligatorio`),
        order_production_type_id: yup
            .number()
            .required(`'Tipo de orden de produccion' es obligatorio`)
            .nullable(),
        is_inactive: yup.mixed().notRequired(),
        branch_id: yup
            .number()
            .required(`'Sucursal' es obligatorio`)
            .nullable(),
        is_leader: yup.mixed().notRequired(),
        base_salary: yup
            .number()
            .typeError(`'Sueldo base' debe ser un número`)
            .min(0, `'Sueldo base' no puede ser negativo`)
            .required(`'Sueldo base' es obligatorio`),
        hours_should_work: yup
            .number()
            .typeError(`'Horas base' debe ser un número`)
            .min(0, `'Horas base' no puede ser negativo`)
            .required(`'Horas base' es obligatorio`),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading || disabled}
            actionBarStart={
                employee?.id ? (
                    <DAuditInfo
                        created_by={employee.created_by}
                        updated_by={employee.updated_by}
                        createdLabel={'Creado por'}
                        updatedLabel={'Actualizado por'}
                    />
                ) : null
            }
            onSubmit={async (data) => {
                try {
                    await mutation({
                        variables: {
                            EmployeeUpsertInput: {
                                id: employee?.id || null,
                                first_name: data.first_name,
                                last_name: data.last_name,
                                is_inactive: Boolean(data.is_inactive),
                                branch_id: data.branch_id,
                                order_production_type_id:
                                    data.order_production_type_id,
                                is_leader: data?.is_leader ? 1 : 0,
                                // Coerce: a number input hands back a string
                                // until yup casts, so normalize before sending.
                                base_salary: Number(data.base_salary) || 0,
                                hours_should_work:
                                    Number(data.hours_should_work) || 0,
                            },
                        },
                    });
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
            onInvalid={async (errors) => {
                console.error(errors);
            }}
        >
            {/*
              Converted from a single DSection to tabs so the admin-only
              Historial tab has somewhere to live. With one content tab the UI
              is materially unchanged for non-admins.
            */}
            <RhTabsWrapper
                tabs={[
                    {
                        label: 'General',
                        fields: [
                            'first_name',
                            'last_name',
                            'order_production_type_id',
                            'branch_id',
                            IS_INACTIVE,
                            IS_LEADER,
                            'base_salary',
                            'hours_should_work',
                        ],
                        content: (
                            <>
                                <RHTextField
                                    name={'first_name'}
                                    label={shortLabel(FIELD_LABELS.first_name)}
                                />
                                <RHTextField
                                    name={'last_name'}
                                    label={shortLabel(FIELD_LABELS.last_name)}
                                />
                                <OrderProductionTypeInput />
                                <BranchInput />
                                <EmployeeInactiveInput />
                                <EmployeeIsLeaderInput />
                                <RHTextField
                                    name={'base_salary'}
                                    label={shortLabel(FIELD_LABELS.base_salary)}
                                    type={'number'}
                                />
                                <RHTextField
                                    name={'hours_should_work'}
                                    label={shortLabel(
                                        FIELD_LABELS.hours_should_work,
                                    )}
                                    type={'number'}
                                />
                            </>
                        ),
                    },
                    // Empty for non-admins, so they get no tab at all.
                    ...historialTab,
                ]}
            />
        </RhFormWrapper>
    );
}
